// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { LanguageModel, OrderModel, OrderSettingsModel } from "@insite/client-framework/Types/ApiModels";
import Clickable from "@insite/mobius/Clickable";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Link, { LinkPresentationProps } from "@store/MobiusOverrides/Link/Link";
import Tooltip, { TooltipPresentationProps } from "@store/MobiusOverrides/Tooltip/Tooltip";
import { getDocumentsWrapper } from "@store/Framework/Common/Utilities/getDocumentsWrapper";
import useTrackFileDownload from "@store/Framework/GoogleAnalytics/hooks/useTrackFileDownload";
import CopyButton, { CopyButtonPresentationProps } from "@store/Library/Components/CopyButton";
import { getDocumentAcknowledgement } from "@store/Framework/Services/documentService";
import StatusBadge, { StatusBadgePresentationProps } from "@store/Library/Components/StatusBadge";
import { orderStatusColorMap } from "@store/Library/Components/statusColorMaps";
import { orderV2Props } from "@store/Themes/common/props/orderV2";
import { tableFilterProps } from "@store/Themes/common/props/table";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import parse from "html-react-parser";
import React, { FC, ReactNode, useMemo, useState } from "react";
import { css } from "styled-components";
import tryJsonParse from "@store/Framework/Common/Utilities/tryJsonParse";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";

interface OrderChanges {
    customerPO?: string;
    carrier?: string;
    deliveryTerms?: string;
    paymentTerms?: string;
    plannedReceiptDate?: string;
}

interface OrderChangeMessages {
    poNumber?: string;
    requestedDate?: string;
    carrier?: string;
    deliveryTerms?: string;
    paymentTerms?: string;
}

export interface OrderInformationCardPresentationProps extends ExpandStyles<OrderInformationCardStyles> {}

export interface OrderInformationCardProps extends OrderInformationCardPresentationProps {
    order: OrderModel;
    showOrderNumber?: boolean;
    showPONumber?: boolean;
    showStatus?: boolean;
    showOrderDate?: boolean;
    showPlant?: boolean;
    showRequestedDeliveryDate?: boolean;
    showCarrier?: boolean;
    showDeliveryTerms?: boolean;
    showPaymentTerms?: boolean;
    showTermsOfSale?: boolean;
    showSoldTo?: boolean;
    showOrderAcknowledgement?: boolean;
    /** When true, shows a warning tooltip next to fields that differ from a pending order change. Off by default. */
    showChangeIndicators?: boolean;
}

export interface OrderInformationCardStyles {
    card?: InjectableCss;
    fieldsWrapper?: InjectableCss;
    headerFieldsWrapper?: InjectableCss;
    field?: InjectableCss;
    compactField?: InjectableCss;
    soldToField?: InjectableCss;
    fieldLabel?: TypographyPresentationProps;
    labelRow?: InjectableCss;
    fieldValue?: TypographyPresentationProps;
    orderNumberFieldValue?: TypographyPresentationProps;
    poNumberFieldValue?: TypographyPresentationProps;
    valueRow?: InjectableCss;
    copyButton?: CopyButtonPresentationProps;
    statusInfoIcon?: IconPresentationProps;
    statusModal?: ModalPresentationProps;
    statusBadge?: StatusBadgePresentationProps;
    changeTooltip?: TooltipPresentationProps;
    termsOfSaleLink?: LinkPresentationProps;
    actionsWrapper?: InjectableCss;
    orderAcknowledgementLink?: LinkPresentationProps;
    orderAcknowledgementLoadingLink?: LinkPresentationProps;
}

export const orderInformationCardStyles: OrderInformationCardStyles = {
    card: mergeProps(orderV2Props.boxWrapper, {
        css: css`
            background-color: ${getColor("common.accent")};
            border: none;
        `,
    }),
    fieldsWrapper: {
        css: css`
            display: flex;
            flex-flow: row wrap;
            gap: ${pxToRem(16)};
        `,
    },
    headerFieldsWrapper: {
        css: css`
            display: flex;
            flex-flow: row wrap;
            gap: ${pxToRem(16)};
            flex-basis: 100%;
            width: 100%;
        `,
    },
    field: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(8)};
            min-width: 0;
            ${breakpointValues(["200px", null, null, "47%"], "flex-basis")}
        `,
    },
    compactField: {
        css: css`
            gap: ${pxToRem(4)};
        `,
    },
    soldToField: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(8)};
            min-width: 0;
            flex-grow: 1;
            ${breakpointValues(["200px", null, null, "100%"], "flex-basis")}
            ${breakpointValues(
                [
                    null,
                    null,
                    null,
                    css`
                        margin-top: ${pxToRem(16)};
                        padding-top: ${pxToRem(16)};
                        border-top: 1px solid ${getColor("common.border")};
                    `,
                ],
                null,
            )}
        `,
    },
    fieldLabel: orderV2Props.infoLabel,
    labelRow: {
        css: css`
            display: flex;
            align-items: start;
            gap: ${pxToRem(4)};
        `,
    },
    valueRow: {
        css: css`
            display: flex;
            align-items: center;
            gap: ${pxToRem(10)};
        `,
    },
    copyButton: {
        extendedStyles: {
            icon: {
                size: 20,
                color: "text.light",
            },
        },
    },
    statusInfoIcon: {
        src: "Info",
        size: 16,
    },
    statusBadge: {
        extendedStyles: {
            wrapper: {
                css: css`
                    white-space: normal;
                    overflow: visible;
                    overflow-wrap: anywhere;
                `,
            },
        },
    },
    changeTooltip: orderV2Props.changeTooltip,
    orderAcknowledgementLink: tableFilterProps.exportLink,
    orderAcknowledgementLoadingLink: tableFilterProps.exportLoadingLink,
    actionsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            flex-basis: 100%;
            gap: ${pxToRem(8)};
            margin-top: ${pxToRem(16)};
            ${breakpointValues(
                [
                    null,
                    null,
                    null,
                    css`
                        padding-top: ${pxToRem(16)};
                        border-top: 1px solid ${getColor("common.border")};
                    `,
                ],
                null,
            )}
        `,
    },
    orderNumberFieldValue: {
        size: 20,
        weight: "bold",
        lineHeight: "1.2",
    },
    poNumberFieldValue: {
        size: 20,
        weight: "bold",
        lineHeight: "1.2",
    },
    termsOfSaleLink: {
        mergeCss: true,
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.medium};
        `,
    },
};

const parseOrderChanges = (orderChangesJson: string | undefined) => {
    if (!orderChangesJson) {
        return undefined;
    }

    return tryJsonParse<OrderChanges>(orderChangesJson);
};

const formatOrderDate = (dateTime: Date | string, language: LanguageModel) =>
    getLocalizedDateTime({
        dateTime: new Date(dateTime),
        language,
        options: { year: "numeric", month: "numeric", day: "numeric" },
    }).toString();

const getOrderNumber = (order: OrderModel, orderSettings: OrderSettingsModel) =>
    orderSettings.showWebOrderNumber
        ? order.webOrderNumber || order.erpOrderNumber
        : order.erpOrderNumber || order.webOrderNumber;

const getSoldToAddress = (order: OrderModel) =>
    [
        order.btCompanyName,
        order.btAddress1,
        order.btAddress2,
        [[order.billToCity, order.billToState].filter(Boolean).join(", "), order.billToPostalCode]
            .filter(Boolean)
            .join(" "),
        order.btCountry,
    ]
        .map(part => part?.trim())
        .filter(Boolean)
        .join(", ");

const getRequestedDateChangeMessage = (
    order: OrderModel,
    orderChanges: OrderChanges | undefined,
    language: LanguageModel,
) => {
    const requestedDeliveryDate = order.requestedDeliveryDateDisplay;
    const requestedDeliveryDateComparison = order.properties.requestedDeliveryDateDisplay;

    if (
        !requestedDeliveryDate ||
        !orderChanges?.plannedReceiptDate ||
        orderChanges.plannedReceiptDate === requestedDeliveryDateComparison
    ) {
        return undefined;
    }

    return translate(
        "The Requested Date has been updated from {0} to {1}",
        formatOrderDate(orderChanges.plannedReceiptDate, language),
        formatOrderDate(requestedDeliveryDate, language),
    );
};

const getOrderChangeMessages = (
    order: OrderModel,
    orderChanges: OrderChanges | undefined,
    language: LanguageModel,
): OrderChangeMessages => {
    const poNumber = orderChanges?.customerPO;
    const currentPoNumber = order.customerPO;
    const carrier = orderChanges?.carrier;
    const currentCarrier = order.properties.carrier;
    const deliveryTerms = orderChanges?.deliveryTerms;
    const currentDeliveryTerms = order.properties.deliveryTerms;
    const paymentTerms = orderChanges?.paymentTerms;
    const currentPaymentTerms = order.properties.paymentTerms;

    return {
        poNumber:
            poNumber && currentPoNumber !== undefined && poNumber !== currentPoNumber
                ? translate("The PO number has been updated from {0} to {1}", poNumber, currentPoNumber)
                : undefined,
        requestedDate: getRequestedDateChangeMessage(order, orderChanges, language),
        carrier:
            carrier && currentCarrier !== undefined && carrier !== currentCarrier
                ? translate("Carrier has been updated from {0} to {1}", carrier, currentCarrier)
                : undefined,
        deliveryTerms:
            deliveryTerms && currentDeliveryTerms !== undefined && deliveryTerms !== currentDeliveryTerms
                ? translate("Delivery Terms has been updated from {0} to {1}", deliveryTerms, currentDeliveryTerms)
                : undefined,
        paymentTerms:
            paymentTerms && currentPaymentTerms !== undefined && paymentTerms !== currentPaymentTerms
                ? translate("The payment Terms have updated from {0} to {1}", paymentTerms, currentPaymentTerms)
                : undefined,
    };
};

const OrderInformationCard: FC<OrderInformationCardProps> = ({
    order,
    showOrderNumber = true,
    showPONumber = true,
    showStatus = true,
    showOrderDate = true,
    showPlant = true,
    showRequestedDeliveryDate = true,
    showCarrier = true,
    showDeliveryTerms = true,
    showPaymentTerms = true,
    showTermsOfSale = true,
    showSoldTo = true,
    showOrderAcknowledgement = true,
    showChangeIndicators = false,
    extendedStyles,
    overridedStyles,
}) => {
    const styles = useExpandProps(orderInformationCardStyles, extendedStyles, overridedStyles);
    const toaster = useToaster();
    const trackFileDownload = useTrackFileDownload();
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [isLoadingOrderAcknowledgement, setIsLoadingOrderAcknowledgement] = useState(false);
    const { orderSettings } = useSettingsCollection();
    const language = useAppSelector(state => state.context.session.language);

    if (!language) {
        return null;
    }

    const orderChanges = useMemo<OrderChanges | undefined>(() => {
        if (!showChangeIndicators) {
            return undefined;
        }

        return parseOrderChanges(order.properties.orderChanges);
    }, [showChangeIndicators, order.properties.orderChanges]);
    const orderStatusDetails = siteMessage("OrderHistory_OrderStatusDescriptions");
    const orderStatusDetailsContent =
        typeof orderStatusDetails === "string" ? parse(orderStatusDetails, parserOptions) : orderStatusDetails;

    const orderNumber = useMemo(() => getOrderNumber(order, orderSettings), [order, orderSettings]);
    const soldToAddress = useMemo(() => getSoldToAddress(order), [order]);
    const changeMessages = useMemo(
        () => getOrderChangeMessages(order, orderChanges, language),
        [order, orderChanges, language],
    );

    const renderChangeTooltip = (message?: string) => {
        if (!showChangeIndicators || !message) {
            return null;
        }

        return <Tooltip text={message} {...styles.changeTooltip} />;
    };

    const renderField = (
        label: string,
        value: ReactNode,
        testSelector: string,
        options?: { labelAdornment?: ReactNode; fieldStyles?: InjectableCss },
    ) => {
        const mergedFieldStyles = options?.fieldStyles ? mergeToNew(styles.field, options.fieldStyles) : styles.field;

        return (
            <StyledWrapper {...mergedFieldStyles}>
                {label.length > 0 && (
                    <StyledWrapper {...styles.labelRow}>
                        <Typography {...styles.fieldLabel}>{label}</Typography>
                        {options?.labelAdornment}
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.valueRow} data-test-selector={testSelector}>
                    {value}
                </StyledWrapper>
            </StyledWrapper>
        );
    };

    const handleOrderAcknowledgementClick = async () => {
        const isSuccess = await getDocumentsWrapper(
            getDocumentAcknowledgement,
            { acknowledgementNumber: order.erpOrderNumber },
            { stateSetter: setIsLoadingOrderAcknowledgement, toasterContext: toaster },
        );

        if (isSuccess) {
            trackFileDownload({ file_category: "Order Acknowledgement" });
        }
    };

    const requestedDeliveryDate = order.requestedDeliveryDateDisplay;
    const carrier = order.properties.carrier;
    const deliveryTerms = order.properties.deliveryTerms;
    const termsOfSaleDocumentLink = order.properties.termsOfSaleDocumentLink;

    const showOrderNumberField = showOrderNumber && Boolean(orderNumber);
    const showPONumberField = showPONumber && Boolean(order.customerPO);
    const isOrderAcknowledgementDisabled = isLoadingOrderAcknowledgement || order.properties.orderApproved !== "true";
    const hasHeaderFields = showOrderNumberField || showPONumberField;

    return (
        <StyledWrapper {...styles.card} data-test-selector="orderInformationCard">
            <StyledWrapper {...styles.fieldsWrapper}>
                {hasHeaderFields && (
                    <StyledWrapper {...styles.headerFieldsWrapper}>
                        {showOrderNumberField &&
                            renderField(
                                translate("Greif Order Number"),
                                <>
                                    <Typography {...styles.orderNumberFieldValue}>{orderNumber}</Typography>
                                    <CopyButton text={orderNumber} {...styles.copyButton} />
                                </>,
                                "orderInformationCard_orderNumber",
                                { fieldStyles: styles.compactField },
                            )}
                        {showPONumberField &&
                            renderField(
                                translate("PO Number"),
                                <>
                                    <Typography {...styles.poNumberFieldValue}>{order.customerPO}</Typography>
                                    <CopyButton text={order.customerPO} {...styles.copyButton} />
                                </>,
                                "orderInformationCard_poNumber",
                                {
                                    fieldStyles: styles.compactField,
                                    labelAdornment: renderChangeTooltip(changeMessages.poNumber),
                                },
                            )}
                    </StyledWrapper>
                )}
                {showStatus &&
                    order.statusDisplay &&
                    renderField(
                        translate("Order Status"),
                        <StatusBadge
                            status={order.status}
                            displayText={order.statusDisplay}
                            colorMap={orderStatusColorMap}
                            {...styles.statusBadge}
                        />,
                        "orderInformationCard_status",
                        {
                            labelAdornment: orderStatusDetails ? (
                                <Clickable
                                    onClick={() => setStatusModalOpen(true)}
                                    title={translate("Order Status Descriptions")}
                                >
                                    <IconMemo
                                        {...styles.statusInfoIcon}
                                        title={translate("Order Status Descriptions")}
                                    />
                                </Clickable>
                            ) : undefined,
                        },
                    )}
                {showOrderDate &&
                    order.orderDate &&
                    renderField(
                        translate("Order Date"),
                        <Typography {...styles.fieldValue}>{formatOrderDate(order.orderDate, language)}</Typography>,
                        "orderInformationCard_orderDate",
                    )}
                {showPlant &&
                    order.properties.warehouse &&
                    renderField(
                        translate("Plant"),
                        <Typography {...styles.fieldValue}>{order.properties.warehouse}</Typography>,
                        "orderInformationCard_plant",
                    )}
                {showRequestedDeliveryDate &&
                    requestedDeliveryDate &&
                    renderField(
                        translate("Requested Delivery Date"),
                        <Typography {...styles.fieldValue}>
                            <LocalizedDateTime
                                dateTime={new Date(requestedDeliveryDate)}
                                options={{ year: "numeric", month: "numeric", day: "numeric" }}
                            />
                        </Typography>,
                        "orderInformationCard_requestedDeliveryDate",
                        { labelAdornment: renderChangeTooltip(changeMessages.requestedDate) },
                    )}
                {showCarrier &&
                    carrier &&
                    renderField(
                        translate("Carrier"),
                        <Typography {...styles.fieldValue}>{order.properties.carrierDescription}</Typography>,
                        "orderInformationCard_carrier",
                        { labelAdornment: renderChangeTooltip(changeMessages.carrier) },
                    )}
                {showDeliveryTerms &&
                    deliveryTerms &&
                    renderField(
                        translate("Delivery Terms"),
                        <Typography {...styles.fieldValue}>{order.properties.deliveryTermsDescription}</Typography>,
                        "orderInformationCard_deliveryTerms",
                        { labelAdornment: renderChangeTooltip(changeMessages.deliveryTerms) },
                    )}
                {showPaymentTerms &&
                    order.terms &&
                    renderField(
                        translate("Payment Terms"),
                        <Typography {...styles.fieldValue}>{order.properties.paymentTermsDescription}</Typography>,
                        "orderInformationCard_paymentTerms",
                        { labelAdornment: renderChangeTooltip(changeMessages.paymentTerms) },
                    )}
                {showTermsOfSale &&
                    termsOfSaleDocumentLink &&
                    renderField(
                        "",
                        <Link href={termsOfSaleDocumentLink} target="_blank" {...styles.termsOfSaleLink}>
                            {translate("Terms of Sale")}
                        </Link>,
                        "orderInformationCard_termsOfSale",
                    )}
                {showSoldTo && soldToAddress && (
                    <StyledWrapper {...styles.soldToField} data-test-selector="orderInformationCard_soldTo">
                        <Typography {...styles.fieldLabel}>{translate("Sold-To Information")}</Typography>
                        <Typography {...styles.fieldValue}>{soldToAddress}</Typography>
                    </StyledWrapper>
                )}
                {showOrderAcknowledgement && (
                    <StyledWrapper {...styles.actionsWrapper}>
                        <Link
                            {...(isLoadingOrderAcknowledgement
                                ? styles.orderAcknowledgementLoadingLink
                                : styles.orderAcknowledgementLink)}
                            data-test-selector="orderInformationCard_orderAcknowledgement"
                            disabled={isOrderAcknowledgementDisabled}
                            onClick={handleOrderAcknowledgementClick}
                        >
                            {translate("Order Acknowledgement")}
                        </Link>
                    </StyledWrapper>
                )}
            </StyledWrapper>
            {orderStatusDetails && (
                <Modal
                    headline={translate("Order Status Descriptions")}
                    isOpen={statusModalOpen}
                    handleClose={() => setStatusModalOpen(false)}
                    {...styles.statusModal}
                >
                    {orderStatusDetailsContent}
                </Modal>
            )}
        </StyledWrapper>
    );
};

export default OrderInformationCard;
